/**
 * HeldContingenciesManager — Admin hub tab "Held contingencies".
 *
 * Wisconsin contingencies the platform stopped updating automatically
 * ("held for attorney attention": no confirmed deadline, unclear cure terms,
 * activity before written notices were recorded, a flag a party raised ...).
 * Left: the held rows across deals, filtered by reason, state and status.
 * Right: the selected row's facts, its full history, and the outcomes the
 * rules allow for the reasons it is held for.
 *
 * cutthe6.com doesn't decide legal questions. The admin records what the
 * parties' own signed documents show. Every outcome opens a confirm dialog
 * that requires a note, an uploaded supporting document for outcomes with
 * legal effect, and an explicit acknowledgement; the server re-checks all of
 * it. Copy: legal's §14 (`WI.admin.held.*`), mirrored verbatim in
 * ../ContingencyHub/contract/contractCopy.js.
 *
 * Copy §15: the lead-paint outcome `record_not_applicable` (status
 * `does_not_apply`; the admin enters the year of original construction the
 * record shows and its source), and the deal-level "Record that the offer
 * ended by a document both parties signed" (RecordEndedDialog), reachable from
 * the selected deal or by transaction ID, since it doesn't need a held row.
 *
 * Contract: src/agents/heldContingenciesAdminAgent.js.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { YEAR_SOURCES, heldContingenciesAdminAgent } from '../../agents/heldContingenciesAdminAgent';
import { contractCopy } from '../ContingencyHub/contract/contractCopy';
import RecordEndedDialog from './RecordEndedDialog';
import styles from './HeldContingenciesManager.module.css';

export const STATUS_LABELS = {
  awaiting_signed_offer: 'Awaiting signed offer',
  open: 'Open',
  no_deadline: 'No confirmed deadline',
  cure_pending: 'Cure period running',
  cure_terms_unclear: 'Cure terms unclear',
  land_division_lapsed: 'Land division deadline passed',
  cured: 'Seller will correct',
  waived: 'Waived',
  treated_as_satisfied: 'Treated as satisfied',
  satisfied: 'Satisfied',
  terminated_by_notice: 'Offer ended',
  terminated_cure_lapsed: 'Offer ended (cure period)',
  terminated_land_division: 'Offer ended (land division)',
  // T17 record_ended: neutral, the documents show how the offer ended (legal §14).
  terminated_admin_record: contractCopy('WI.timeline.deal_terminated'),
  moot: 'No longer applies',
  // Copy §15.1 record_not_applicable.
  does_not_apply: contractCopy('WI.hub.status.does_not_apply'),
};

const EVENT_LABELS = {
  seeded: 'Tracking started',
  anchored: 'Deadline counted from the signed offer',
  notice_delivered: 'Written notice delivered',
  treated_as_satisfied: 'Treated as satisfied (deadline passed)',
  cure_lapsed: 'Cure period ended',
  land_division_lapsed: 'Land division deadline passed',
  moot: 'No longer applies',
  deal_terminated: 'Offer recorded as ended',
  attention_set: 'Held for attorney attention',
  admin_resolution: 'Admin resolution',
  offplatform_notice_reported: 'Party reported a notice delivered another way',
  cure_disputed: 'Buyer disputed the cure',
  land_division_disputed: 'Party disputed the land division record',
  not_applicable_disputed: contractCopy('WI.attention.not_applicable_disputed.short'),
};

/** Admin labels for `year_source` (copy §15.1 rule 4: the records that count). */
export const YEAR_SOURCE_LABELS = {
  assessor_record: "Municipal or county assessor's or tax record",
  building_permit: 'Building permit',
  occupancy_certificate: 'Certificate of occupancy',
  signed_by_both: 'Amendment or other document both parties signed',
};

const OUTCOME_RECORD_NOT_APPLICABLE = 'record_not_applicable';
/** Copy §15.1 rule 4: earlier years never qualify; 1978 only with a permit or construction-start record. */
const MIN_YEAR_BUILT = 1978;

/** Transaction statuses where the offer can't be recorded as ended (copy §15.2 rule 2). */
const ENDED_DEAL_STATUSES = ['terminated', 'completed'];

const STATUS_FILTERS = ['', 'open', 'no_deadline', 'cure_pending', 'cure_terms_unclear', 'land_division_lapsed',
  'waived', 'cured', 'satisfied', 'treated_as_satisfied', 'does_not_apply', 'terminated_by_notice',
  'terminated_admin_record', 'moot'];

/** Statuses where the buyer already delivered a notice under the contingency (copy §14.1). */
const AFTER_NOTICE_STATUSES = ['cure_pending', 'cure_terms_unclear'];

const hasReason = (contingency, reason) => (contingency?.active_reasons || []).some((r) => r.reason === reason);

/**
 * The copy §14.1 help variant for an outcome on this row: legal's
 * `.help.after_notice` (record_waived once the buyer delivered a notice) and
 * `.help.cure_terms_unclear` (record_cured when the offer doesn't say whether
 * the seller may cure), copy §15.1's `.help.lead_paint_year_unknown`
 * (resume_tracking on a row held because the year built is unknown), else `.help`.
 *
 * @param {string} outcome
 * @param {{contract_status?: string, active_reasons?: Array<{reason: string}>}} [contingency]
 * @returns {string} the copy key
 */
export function outcomeHelpKey(outcome, contingency) {
  const base = `WI.admin.held.outcome.${outcome}.help`;
  const status = contingency?.contract_status;
  if (outcome === 'record_waived' && AFTER_NOTICE_STATUSES.includes(status)) return `${base}.after_notice`;
  if (outcome === 'record_cured'
    && (status === 'cure_terms_unclear' || hasReason(contingency, 'cure_terms_unclear'))) {
    return `${base}.cure_terms_unclear`;
  }
  if (outcome === 'resume_tracking' && hasReason(contingency, 'lead_paint_year_unknown')) {
    return `${base}.lead_paint_year_unknown`;
  }
  return base;
}

/** The note hint for an outcome: copy §15.1's own for record_not_applicable, else §14.1's. */
export const noteHintKey = (outcome) => (
  outcome === OUTCOME_RECORD_NOT_APPLICABLE ? 'WI.admin.held.note.hint.record_not_applicable' : 'WI.admin.held.note.hint'
);

/**
 * The year a record_not_applicable record shows, when the client can accept it
 * (a whole year from 1978 to this year), else null. The server re-checks it.
 *
 * @param {string|number} value
 * @param {number} [thisYear]
 * @returns {number|null}
 */
export function validYearBuilt(value, thisYear = new Date().getFullYear()) {
  const text = String(value ?? '').trim();
  if (!/^\d{4}$/.test(text)) return null;
  const year = Number(text);
  return year >= MIN_YEAR_BUILT && year <= thisYear ? year : null;
}

export const outcomeLabel = (outcome) => contractCopy(`WI.admin.held.outcome.${outcome}.label`) || outcome;
export const outcomeHelp = (outcome, contingency) => (
  contractCopy(outcomeHelpKey(outcome, contingency)) || contractCopy(`WI.admin.held.outcome.${outcome}.help`) || ''
);

const statusLabel = (s) => STATUS_LABELS[s] || s || '—';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago',
  }) + ' CT';
};

const formatDate = (isoDate) => {
  if (!isoDate) return '—';
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return isoDate;
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
};

/** Admin-facing message for a failed request. */
export function errorMessage(err, fallback) {
  if (err?.status === 403) return 'You need admin access to review held contingencies.';
  const detail = err?.detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  return err?.message || fallback;
}

function ReasonTags({ reasons }) {
  if (!reasons || reasons.length === 0) return null;
  return (
    <span className={styles.tags}>
      {reasons.map((r) => (
        <span key={r.reason} className={styles.tag} title={r.reason} data-testid="held-reason-tag">
          {r.short_label || r.reason}
        </span>
      ))}
    </span>
  );
}

const YEAR_HINT = '1979 or later. 1978 only with a permit or other record showing construction began on or after '
  + 'January 1, 1978.';
const YEAR_REJECTED_HINT = 'This outcome is only for a home built in 1978 or later. If the record shows an earlier year, '
  + "leave the contingency held, or release the hold and keep the signed offer's deadline.";

// ── Confirm dialog ──────────────────────────────────────────────────────────
function ResolveDialog({ detail, option, onClose, onDone }) {
  const uid = useId();
  const noteRef = useRef(null);
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [deadlineDate, setDeadlineDate] = useState('');
  const [yearBuilt, setYearBuilt] = useState('');
  const [yearSource, setYearSource] = useState('');
  const [began1978, setBegan1978] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const { outcome } = option;
  const noteMissing = note.trim().length < 10;
  const fileMissing = option.evidence_required && !file;
  const deadlineMissing = option.needs_deadline && !deadlineDate;
  const needsYear = outcome === OUTCOME_RECORD_NOT_APPLICABLE;
  const year = needsYear ? validYearBuilt(yearBuilt) : null;
  const yearRejected = needsYear && yearBuilt.length === 4 && year == null;
  // Copy §15.1 rule 4: a record showing 1978 counts only with a record that construction began in 1978 or later.
  const is1978 = year === MIN_YEAR_BUILT;
  const yearMissing = needsYear && (year == null || !yearSource || (is1978 && !began1978));
  const yearSources = Array.isArray(option.year_sources) && option.year_sources.length ? option.year_sources : YEAR_SOURCES;
  const canSubmit = !saving && confirmed && !noteMissing && !fileMissing && !deadlineMissing && !yearMissing;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      let evidenceFileId;
      if (file) {
        const uploaded = await heldContingenciesAdminAgent.uploadEvidence(detail.contingency.id, file);
        evidenceFileId = uploaded.file_id;
      }
      const result = await heldContingenciesAdminAgent.resolve(detail.contingency.id, {
        outcome,
        note,
        expectedVersion: detail.contingency.version,
        evidenceFileId,
        deadlineDate: option.needs_deadline ? deadlineDate : undefined,
        yearBuiltShown: needsYear ? year : undefined,
        yearSource: needsYear ? yearSource : undefined,
        constructionBegan1978OrLater: needsYear && is1978 ? began1978 : undefined,
      });
      onDone(result, option);
    } catch (err) {
      setError(errorMessage(err, 'The outcome could not be recorded. Nothing was changed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={saving ? () => {} : onClose}
      title={`${outcomeLabel(outcome)}: ${detail.contingency.heading || detail.contingency.catalog_key}`}
      size="md"
      initialFocusRef={noteRef}
      dismissOnOverlayClick={false}
    >
      <form onSubmit={submit} data-testid="held-resolve-dialog">
        <p className={styles.notice} data-testid="held-dialog-intro">{contractCopy('WI.admin.held.intro')}</p>
        <section className={styles.effect} aria-labelledby={`${uid}-effect`}>
          <h4 id={`${uid}-effect`} className={styles.effectTitle}>What this records</h4>
          <p className={styles.confirmText} data-testid="held-dialog-help">{outcomeHelp(outcome, detail.contingency)}</p>
          <dl className={styles.effectList}>
            <dt>Status now</dt>
            <dd>{statusLabel(detail.contingency.contract_status)}</dd>
            <dt>Status after</dt>
            <dd data-testid="held-dialog-to-status"><strong>{statusLabel(option.to_status)}</strong></dd>
            <dt>Held for</dt>
            <dd><ReasonTags reasons={detail.contingency.active_reasons} /></dd>
          </dl>
        </section>

        {option.ends_offer && (
          <p className={styles.errorBox} role="note" data-testid="held-ends-offer-warning">
            {contractCopy('WI.admin.held.ends_offer_warning')}
          </p>
        )}

        {option.needs_deadline && (
          <>
            <label className={styles.label} htmlFor={`${uid}-deadline`}>
              Deadline in the signed amendment (11:59 p.m. Central that day; required)
            </label>
            <input
              id={`${uid}-deadline`}
              type="date"
              className={styles.input}
              value={deadlineDate}
              onChange={(e) => setDeadlineDate(e.target.value)}
              aria-required="true"
              data-testid="held-deadline-date"
            />
          </>
        )}

        {needsYear && (
          <fieldset className={styles.yearFields} data-testid="held-year-fields">
            <legend className={styles.label}>What the record shows</legend>
            <label className={styles.label} htmlFor={`${uid}-year`}>
              Year of original construction (not an &quot;effective&quot; year; required)
            </label>
            <input
              id={`${uid}-year`}
              type="text"
              inputMode="numeric"
              maxLength={4}
              className={styles.input}
              value={yearBuilt}
              onChange={(e) => setYearBuilt(e.target.value.replace(/\D/g, ''))}
              aria-required="true"
              aria-invalid={yearRejected ? 'true' : undefined}
              aria-describedby={`${uid}-year-hint`}
              data-testid="held-year-built"
            />
            <p className={yearRejected ? styles.errorBox : styles.hint} id={`${uid}-year-hint`} data-testid="held-year-hint">
              {yearRejected ? YEAR_REJECTED_HINT : YEAR_HINT}
            </p>
            <label className={styles.label} htmlFor={`${uid}-source`}>Record (required)</label>
            <select
              id={`${uid}-source`}
              className={styles.input}
              value={yearSource}
              onChange={(e) => setYearSource(e.target.value)}
              aria-required="true"
              data-testid="held-year-source"
            >
              <option value="">Choose the record…</option>
              {yearSources.map((src) => <option key={src} value={src}>{YEAR_SOURCE_LABELS[src] || src}</option>)}
            </select>
            {is1978 && (
              <label className={styles.confirmCheck} data-testid="held-year-1978">
                <input type="checkbox" checked={began1978} onChange={(e) => setBegan1978(e.target.checked)} />
                <span>
                  A building permit or other record shows that construction began on or after January 1, 1978.
                  (Required for 1978; otherwise leave the contingency held.)
                </span>
              </label>
            )}
          </fieldset>
        )}

        <label className={styles.label} htmlFor={`${uid}-file`}>
          Supporting document {option.evidence_required ? '(required)' : '(optional)'}
        </label>
        <input
          id={`${uid}-file`}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          className={styles.input}
          onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
          aria-required={option.evidence_required ? 'true' : undefined}
          aria-describedby={`${uid}-file-hint`}
          data-testid="held-evidence-input"
        />
        <p className={styles.hint} id={`${uid}-file-hint`}>
          {contractCopy(option.evidence_required ? 'WI.admin.held.evidence.required' : 'WI.admin.held.evidence.optional')}
        </p>

        <label className={styles.label} htmlFor={`${uid}-note`}>Note (required)</label>
        <textarea
          id={`${uid}-note`}
          ref={noteRef}
          className={styles.textarea}
          rows={3}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-required="true"
          aria-describedby={`${uid}-note-hint`}
          data-testid="held-note"
        />
        <p className={styles.hint} id={`${uid}-note-hint`} data-testid="held-note-hint">
          {contractCopy(noteHintKey(outcome))}
        </p>

        <label className={styles.confirmCheck} data-testid="held-confirm">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          <span>{contractCopy('WI.admin.held.confirm')}</span>
        </label>

        {error && <p className={styles.errorBox} role="alert" data-testid="held-resolve-error">{error}</p>}

        <div className={styles.confirmActions}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.primaryBtn} ${option.ends_offer ? styles.dangerBtn : ''}`}
            disabled={!canSubmit}
            data-testid="held-resolve-submit"
          >
            {saving ? 'Recording…' : 'Record'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Detail ──────────────────────────────────────────────────────────────────
function HeldDetail({ contingencyId, reloadKey, onChoose, onRecordEnded }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileError, setFileError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await heldContingenciesAdminAgent.get(contingencyId));
    } catch (err) {
      setDetail(null);
      setError(errorMessage(err, 'The contingency could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [contingencyId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const download = async (a) => {
    setFileError(null);
    try {
      await heldContingenciesAdminAgent.downloadAttachment(a.file_id, a.filename);
    } catch (err) {
      setFileError(errorMessage(err, 'The file could not be opened.'));
    }
  };

  if (loading && !detail) {
    return <p className={styles.muted} role="status" data-testid="held-detail-loading">Loading contingency…</p>;
  }
  if (error) {
    return (
      <div className={styles.errorBox} role="alert" data-testid="held-detail-error">
        {error}{' '}
        <button type="button" className={styles.linkBtn} onClick={load}>Retry</button>
      </div>
    );
  }
  if (!detail) return null;

  const c = detail.contingency;
  const t = detail.transaction;
  return (
    <article className={styles.detail} data-testid="held-detail" aria-busy={loading}>
      <header className={styles.detailHeader}>
        <div>
          <p className={styles.eyebrow}>{t.property_address || `Transaction #${t.id}`} · {t.state}</p>
          <h3 className={styles.detailTitle}>{c.heading || c.catalog_key}</h3>
        </div>
        <span className={styles.pill} data-testid="held-detail-status">{statusLabel(c.contract_status)}</span>
      </header>

      <dl className={styles.facts}>
        <div><dt>Deadline shown</dt><dd>{c.deadline_local ? `${formatDate(c.deadline_local)}, 11:59 p.m. CT` : 'None'}</dd></div>
        <div><dt>Deadline basis</dt><dd>{c.deadline_basis || '—'}</dd></div>
        <div><dt>Cure right</dt><dd>{c.cure?.right || '—'}{c.cure?.deadline_local ? ` (by ${formatDate(c.cure.deadline_local)})` : ''}</dd></div>
        <div><dt>Held since</dt><dd>{formatDateTime(c.held_since)}</dd></div>
        <div><dt>Deal</dt><dd>#{t.id} · {t.status}{t.deal_ended ? ' (offer ended)' : ''}</dd></div>
        <div><dt>Buyer</dt><dd>{t.buyer?.name || '—'}{t.buyer?.email ? ` · ${t.buyer.email}` : ''}</dd></div>
        <div><dt>Seller</dt><dd>{t.seller?.name || '—'}{t.seller?.email ? ` · ${t.seller.email}` : ''}</dd></div>
      </dl>

      <div>
        <h4 className={styles.subheading}>Held for</h4>
        {detail.held ? (
          <ReasonTags reasons={c.active_reasons} />
        ) : (
          <p className={styles.muted} data-testid="held-not-held">Not held now. The history below is kept.</p>
        )}
      </div>

      <section className={styles.actions} aria-labelledby="held-outcomes-heading">
        <h4 id="held-outcomes-heading" className={styles.subheading}>Record what the parties&apos; documents show</h4>
        {detail.outcomes.length === 0 ? (
          <p className={styles.muted} data-testid="held-no-outcomes">
            No outcome can be recorded for this contingency now.
          </p>
        ) : (
          <ul className={styles.outcomeList}>
            {detail.outcomes.map((o) => (
              <li key={o.outcome}>
                <button
                  type="button"
                  className={`${styles.outcomeBtn} ${o.ends_offer ? styles.outcomeDanger : ''}`}
                  onClick={() => onChoose(detail, o)}
                  data-testid={`held-outcome-${o.outcome}`}
                >
                  <span className={styles.outcomeLabel}>{outcomeLabel(o.outcome)}</span>
                  <span className={styles.outcomeMeta}>
                    {o.evidence_required ? 'Signed document required' : 'No document required'}
                    {' · '}then: {statusLabel(o.to_status)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {detail.refused_outcomes.length > 0 && (
          <p className={styles.muted} data-testid="held-refused-outcomes">
            Not available for these reasons: {detail.refused_outcomes.map((r) => outcomeLabel(r.outcome)).join('; ')}.
          </p>
        )}
      </section>

      {!t.deal_ended && !ENDED_DEAL_STATUSES.includes(t.status) && (
        <section className={styles.actions} aria-labelledby="held-deal-actions-heading" data-testid="held-deal-actions">
          <h4 id="held-deal-actions-heading" className={styles.subheading}>Transaction</h4>
          <button
            type="button"
            className={`${styles.outcomeBtn} ${styles.outcomeDanger}`}
            onClick={() => onRecordEnded(t)}
            data-testid="held-record-ended"
          >
            <span className={styles.outcomeLabel}>{contractCopy('WI.admin.deal_end.label')}</span>
            <span className={styles.outcomeMeta}>Signed document required · then: the offer is recorded as ended</span>
          </button>
        </section>
      )}

      <details className={styles.details} open data-testid="held-history">
        <summary>History ({detail.events.length})</summary>
        <ol className={styles.historyList}>
          {detail.events.map((e) => (
            <li key={e.id} className={styles.historyItem} data-testid="held-history-item">
              <span className={styles.historyWhen}>{formatDateTime(e.at)}</span>
              <span className={styles.historyWho}>
                {e.actor_type === 'admin' ? `Admin${e.actor?.name ? `: ${e.actor.name}` : ''}` : e.actor_type}
                {e.dry_run ? ' (dry run)' : ''}
              </span>
              <span className={styles.historyChange}>
                {EVENT_LABELS[e.event_type] || e.event_type}
                {e.from_status !== e.to_status && e.to_status
                  ? ` · ${statusLabel(e.from_status)} → ${statusLabel(e.to_status)}` : ''}
              </span>
              <span className={styles.historyNote}>
                {e.reason}
                {e.details?.evidence_sha256 && (
                  <span className={styles.muted}> Evidence SHA-256 {e.details.evidence_sha256.slice(0, 12)}…</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      </details>

      {(detail.notices.length > 0 || detail.attachments.length > 0) && (
        <details className={styles.details} data-testid="held-files">
          <summary>Notices and files ({detail.notices.length + detail.attachments.length})</summary>
          <ul className={styles.fileList}>
            {detail.notices.map((n) => (
              <li key={`n${n.id}`}>
                Notice #{n.id}: {n.notice_type} by {n.actor_role}, {formatDateTime(n.delivered_at)}
                {n.on_time === false ? ' (after the deadline)' : ''}
              </li>
            ))}
            {detail.attachments.map((a) => (
              <li key={`a${a.file_id}`}>
                <button type="button" className={styles.linkBtn} onClick={() => download(a)}>
                  {a.filename}
                </button>{' '}
                <span className={styles.muted}>({a.uploader_role}, {a.purpose}, SHA-256 {a.sha256.slice(0, 12)}…)</span>
              </li>
            ))}
          </ul>
          {fileError && <p className={styles.errorBox} role="alert">{fileError}</p>}
        </details>
      )}
    </article>
  );
}

// ── Manager ─────────────────────────────────────────────────────────────────
export default function HeldContingenciesManager() {
  const uid = useId();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ reason: '', state: '', status: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [target, setTarget] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  // Deal-level record (copy §15.2): {transactionId, initial} while its dialog is open.
  const [dealTarget, setDealTarget] = useState(null);
  const [lookupId, setLookupId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await heldContingenciesAdminAgent.list(filters);
      if (!result || !Array.isArray(result.items)) throw new Error('The held contingencies could not be loaded.');
      setData(result);
      setSelectedId((prev) => {
        if (prev && result.items.some((i) => i.contingency_id === prev)) return prev;
        return result.items[0]?.contingency_id ?? null;
      });
    } catch (err) {
      setError(errorMessage(err, 'The held contingencies could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDone = (result, option) => {
    setTarget(null);
    setAnnouncement(
      `Recorded: ${outcomeLabel(option.outcome)}. Status is now ${statusLabel(result?.event?.to_status || option.to_status)}. Both parties were notified.`,
    );
    setReloadKey((k) => k + 1);
    load();
  };

  const handleDealDone = () => {
    setDealTarget(null);
    setAnnouncement(contractCopy('WI.admin.deal_end.done'));
    setReloadKey((k) => k + 1);
    load();
  };

  const openDealById = (e) => {
    e.preventDefault();
    const id = Number(lookupId);
    if (!Number.isInteger(id) || id <= 0) return;
    setDealTarget({ transactionId: id, initial: null });
  };

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const items = data?.items || [];
  const reasonOptions = data?.reasons || [];
  const states = Object.keys(data?.facets?.states || {}).sort();

  return (
    <div className={styles.manager} data-testid="held-contingencies-manager">
      <div className={styles.visuallyHidden} aria-live="polite" aria-atomic="true">{announcement}</div>

      <div className={styles.toolbar}>
        <h2 className={styles.heading}>Held contingencies</h2>
        <label className={styles.filterLabel} htmlFor={`${uid}-reason`}>Reason</label>
        <select id={`${uid}-reason`} className={styles.filter} value={filters.reason}
          onChange={setFilter('reason')} data-testid="held-filter-reason">
          <option value="">All reasons</option>
          {reasonOptions.map((r) => (
            <option key={r.reason} value={r.reason}>
              {r.short_label}{data?.facets?.reasons?.[r.reason] ? ` (${data.facets.reasons[r.reason]})` : ''}
            </option>
          ))}
        </select>
        <label className={styles.filterLabel} htmlFor={`${uid}-state`}>State</label>
        <select id={`${uid}-state`} className={styles.filterNarrow} value={filters.state}
          onChange={setFilter('state')} data-testid="held-filter-state">
          <option value="">All</option>
          {(states.length ? states : ['WI']).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className={styles.filterLabel} htmlFor={`${uid}-status`}>Status</label>
        <select id={`${uid}-status`} className={styles.filter} value={filters.status}
          onChange={setFilter('status')} data-testid="held-filter-status">
          {STATUS_FILTERS.map((s) => <option key={s || 'all'} value={s}>{s ? statusLabel(s) : 'All statuses'}</option>)}
        </select>
        <button type="button" className={styles.secondaryBtn} onClick={load} disabled={loading}>Refresh</button>
        <span className={styles.count} aria-live="polite">
          {loading ? 'Loading…' : `${data?.total ?? 0} held`}
        </span>
      </div>

      <p className={styles.notice} data-testid="held-intro">{contractCopy('WI.admin.held.intro')}</p>

      <form className={styles.dealLookup} onSubmit={openDealById} data-testid="held-deal-lookup">
        <label className={styles.filterLabel} htmlFor={`${uid}-txn`}>
          {contractCopy('WI.admin.deal_end.label')} (any transaction, held or not). Transaction ID
        </label>
        <input
          id={`${uid}-txn`}
          type="text"
          inputMode="numeric"
          className={styles.filterNarrow}
          value={lookupId}
          onChange={(e) => setLookupId(e.target.value.replace(/\D/g, ''))}
          data-testid="held-deal-lookup-id"
        />
        <button type="submit" className={styles.secondaryBtn} disabled={!lookupId} data-testid="held-deal-lookup-open">
          Open
        </button>
      </form>

      {announcement && <p className={styles.success} role="status" data-testid="held-success">{announcement}</p>}

      {loading && !data && <p className={styles.muted} role="status" data-testid="held-loading">Loading held contingencies…</p>}
      {error && (
        <div className={styles.errorBox} role="alert" data-testid="held-error">
          {error}{' '}
          <button type="button" className={styles.linkBtn} onClick={load}>Retry</button>
        </div>
      )}

      {data && !error && (
        <div className={styles.layout}>
          <section className={styles.queue} aria-label="Held contingencies">
            {items.length === 0 ? (
              <p className={styles.muted} data-testid="held-empty">No contingency is held for these filters.</p>
            ) : (
              <ul className={styles.queueList}>
                {items.map((item) => (
                  <li key={item.contingency_id}>
                    <button
                      type="button"
                      className={`${styles.queueItem} ${item.contingency_id === selectedId ? styles.queueItemActive : ''}`}
                      onClick={() => setSelectedId(item.contingency_id)}
                      aria-current={item.contingency_id === selectedId ? 'true' : undefined}
                      data-testid={`held-item-${item.contingency_id}`}
                    >
                      <span className={styles.queueTop}>
                        <span className={styles.queueTitle}>{item.heading || item.catalog_key}</span>
                        <span className={styles.pill}>{statusLabel(item.contract_status)}</span>
                      </span>
                      <span className={styles.queueMeta}>
                        {item.property_address || `Transaction #${item.transaction_id}`} · {item.state}
                        {item.deal_ended ? ' · offer ended' : ''}
                      </span>
                      <ReasonTags reasons={item.active_reasons} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className={styles.detailPane} aria-label="Selected contingency">
            {selectedId != null ? (
              <HeldDetail
                key={selectedId}
                contingencyId={selectedId}
                reloadKey={reloadKey}
                onChoose={(detail, option) => setTarget({ detail, option })}
                onRecordEnded={(t) => setDealTarget({ transactionId: t.id, initial: t })}
              />
            ) : (
              <p className={styles.muted}>Select a contingency to see its history.</p>
            )}
          </section>
        </div>
      )}

      {target && (
        <ResolveDialog
          detail={target.detail}
          option={target.option}
          onClose={() => setTarget(null)}
          onDone={handleDone}
        />
      )}

      {dealTarget && (
        <RecordEndedDialog
          transactionId={dealTarget.transactionId}
          initial={dealTarget.initial}
          onClose={() => setDealTarget(null)}
          onDone={handleDealDone}
        />
      )}
    </div>
  );
}
